/**
 * Utility functions for simulation API calls with usage limit handling
 */

const API_URL = import.meta.env.VITE_API_URL || '';

export interface UsageLimitError {
  error: string;
  upgrade_required: boolean;
}

export interface SimulationResponse {
  simulation_id: number;
  message: string;
  status: string;
}

export class UsageLimitExceededError extends Error {
  public readonly upgrade_required = true;
  
  constructor(message: string) {
    super(message);
    this.name = 'UsageLimitExceededError';
  }
}

/**
 * Create a new simulation with proper error handling for usage limits
 */
export const createSimulation = async (simulationData: any): Promise<SimulationResponse> => {
  const response = await fetch(`${API_URL}/simulations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(simulationData)
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    // Check if it's a usage limit error
    if (data.upgrade_required) {
      throw new UsageLimitExceededError(`${data.error || data.detail} Please upgrade your plan to continue.`);
    }
    throw new Error(data.error || data.detail || 'Failed to start simulation');
  }
  
  return data;
};

/**
 * Rerun an existing simulation with proper error handling for usage limits
 */
export const rerunSimulation = async (simulationId: number): Promise<SimulationResponse> => {
  const response = await fetch(`${API_URL}/simulations/${simulationId}/rerun`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    // Check if it's a usage limit error
    if (data.upgrade_required) {
      throw new UsageLimitExceededError(`${data.error || data.detail} Please upgrade your plan to continue.`);
    }
    throw new Error(data.error || data.detail || 'Failed to rerun simulation');
  }
  
  return data;
};

export type SurveyMode = 'ai_sim' | 'human';

/**
 * Parse questions with proper error handling for usage limits.
 *
 * Pass surveyMode='human' from the human-survey paste-raw-text flow
 * (SurveyBuilder) so the backend uses the human Pydantic schema (which
 * includes dropdown / image_hotspot / photo_upload + UX-only fields).
 * AI-simulation callers leave the default 'ai_sim'.
 */
export const parseQuestions = async (
  questions: string[],
  goal: string = '',
  context: string = '',
  question_images?: Record<number, string[]>,
  advanced_parsing: boolean = false,
  question_types?: Record<number, string>,
  surveyMode: SurveyMode = 'ai_sim',
): Promise<{task_id: string; questions_count: number}> => {
  const response = await fetch(`${API_URL}/parse-questions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      questions,
      goal,
      context,
      question_images,
      advanced_parsing,
      question_types,
      survey_mode: surveyMode,
    })
  });

  const data = await response.json();
  
  if (!response.ok) {
    // Check if it's a usage limit error
    if (data.upgrade_required) {
      throw new UsageLimitExceededError(`${data.error || data.detail} Please upgrade your plan to continue.`);
    }
    throw new Error(data.error || data.detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  return data;
};


export interface QuestionTag {
  id: string;
  start_idx: number;
  end_idx: number;
  anchor_start: string;
  anchor_end: string;
  type: string;
  needs_image: boolean;
  image_level: 'question' | 'option' | 'none';
}

/**
 * Real-time question splitting + tagging via Claude Haiku.
 * Sends raw unsplit text; AI identifies question boundaries and returns
 * character offsets, anchor words, type, and image-need flag.
 */
export const tagQuestionsRealtime = async (
  rawText: string,
  goal: string = '',
  context: string = '',
  signal?: AbortSignal,
  surveyMode: SurveyMode = 'ai_sim',
): Promise<{ success: boolean; questions: QuestionTag[] }> => {
  const response = await fetch(`${API_URL}/parse-questions/tag-realtime`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ raw_text: rawText, goal, context, survey_mode: surveyMode }),
    signal,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || data.error || `HTTP ${response.status}`);
  }

  return data;
};

/**
 * Unified realtime parsing: Haiku split + boundary resolution + parallel Sonnet parse.
 * Returns fully-parsed QuestionSchema[] directly (no Celery / WebSocket).
 */
export const parseQuestionsRealtime = async (
  rawText: string,
  goal: string = '',
  context: string = '',
  signal?: AbortSignal,
  surveyMode: SurveyMode = 'ai_sim',
): Promise<{ success: boolean; questions: ParsedQuestionFromAPI[] }> => {
  const response = await fetch(`${API_URL}/parse-questions/parse-realtime`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ raw_text: rawText, goal, context, survey_mode: surveyMode }),
    signal,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || data.error || `HTTP ${response.status}`);
  }

  return data;
};

export interface ParsedQuestionFromAPI {
  id?: string;
  text: string;
  type: string;
  choices?: string[];
  scale_min?: number;
  scale_max?: number;
  max_selections?: number;
  ranking_count?: number;
  text_limit?: number;
  allow_other?: boolean;
  image_paths?: string[];
  image_descriptions?: string[];
  image_attachment_level?: string;
  option_images?: Record<string, string>;
  matrix_rows?: string[];
  matrix_columns?: string[];
  maxdiff_items?: string[];
  conjoint_attributes?: Array<{ name: string; levels: string[] }>;
  conjoint_description?: string;
  description?: string;
}

/**
 * Handle API errors consistently across forms
 */
export const handleApiError = (error: unknown, setError: (message: string) => void): void => {
  if (error instanceof UsageLimitExceededError) {
    setError(error.message);
  } else if (error instanceof Error) {
    setError(error.message);
  } else {
    setError('An unexpected error occurred. Please try again.');
  }
};

/**
 * Generate AI descriptions for a list of image paths
 * Used when users edit images in parsed questions
 */
export const generateImageDescriptions = async (imagePaths: string[]): Promise<{
  success: boolean;
  descriptions: string[];
  processed_count: number;
}> => {
  const response = await fetch(`${API_URL}/api/question-images/generate-descriptions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ image_paths: imagePaths })
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || data.detail || 'Failed to generate descriptions');
  }
  
  return data;
};